import './toast.css';

/**
 * Global, light-touch notifications - replaces the blocking `alert()`
 * this app used to reach for everywhere, from "you're now the host"
 * (a status update, not something to interrupt anyone for) down to
 * "couldn't start that party" (still just a message, not worth
 * freezing the whole page over). Self-mounting (appends its own
 * container to `document.body` on first use) rather than something
 * Shell.ts wires up like musicBar/MusicBar - there's no per-party
 * state to attach here, just a place to drop text that outlives
 * whichever view happened to trigger it, so a plain module-level
 * singleton is all this needs.
 */

let container: HTMLElement | null = null;

function getContainer(): HTMLElement {
  if (!container) {
    container = document.createElement('div');
    container.className = 'toastContainer';
    document.body.appendChild(container);
  }
  return container;
}

export type ToastVariant = 'info' | 'error';

export interface ToastOptions {
  /** 'error' adds a left accent - still the same restrained pill, not
   *  a red banner, since even a failed "start a party" click doesn't
   *  warrant this app suddenly shouting. Default 'info'. */
  variant?: ToastVariant;
  /** Milliseconds before it fades back out on its own - there's no
   *  manual dismiss button, same as Player.ts's own .requestBanner. */
  duration?: number;
}

const DEFAULT_DURATION_MS = 4500;

export function showToast(message: string, options: ToastOptions = {}): void {
  const { variant = 'info', duration = DEFAULT_DURATION_MS } = options;

  const el = document.createElement('div');
  el.className = `toast${variant === 'error' ? ' toast--error' : ''}`;
  el.textContent = message;
  getContainer().appendChild(el);

  // Two rAFs, not one: the element needs a full layout/style pass in
  // its *starting* state (opacity 0) before the class add is what
  // triggers the transition - adding it in the very same frame the
  // node was inserted can get coalesced with the insert itself, and
  // the "fade in" never visibly happens.
  requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('toastIn')));

  setTimeout(() => {
    el.classList.remove('toastIn');
    el.classList.add('toastOut');
    el.addEventListener('transitionend', () => el.remove(), { once: true });
    // Belt-and-suspenders for prefers-reduced-motion (transition: none
    // there - see toast.css) - 'transitionend' never fires with no
    // transition running, which would otherwise leave this in the DOM
    // forever instead of just skipping straight to removed.
    setTimeout(() => el.remove(), 300);
  }, duration);
}
